import { useEffect, useRef, useCallback } from 'react';
import { buildYourTourApiUrl, fetchYourTourApi } from '../utils/ApiHelpers';

// How often to poll the heartbeat endpoint (in milliseconds).
const HEARTBEAT_INTERVAL_MS = 5000;

/**
 * useHeartbeat - Polls the lightweight heartbeat endpoint to detect CMS changes.
 *
 * The heartbeat endpoint returns four version counters:
 * - mapStructure: Changes when map structure changes (SVG, levels, etc.)
 * - mapOccupancy: Changes when residence status changes
 * - residence: Changes when residence status or residents change
 * - communityConfig: Changes when the community node is updated
 *
 * Polling is visibility-aware: pauses when the browser tab is hidden,
 * resumes immediately (with an instant check) when the tab becomes visible.
 *
 * @param {string} urlSlug - The community URL slug
 * @param {string} platform - The current platform (yt, kiosk, siteSee, etc.)
 * @param {object} options
 * @param {Function} options.onMapStructureChange - Called when map structure version changes
 *   (triggers full map refetch)
 * @param {Function} options.onMapOccupancyChange - Called when map occupancy version changes
 *   (triggers lightweight occupancy-only refetch)
 * @param {Function} options.onResidenceVersionChange - Called when residence version changes
 *   (triggers residence detail refetch)
 * @param {Function} options.onCommunityConfigChange - Called when community config version
 *   changes (triggers community config refetch so feature flags update live)
 * @param {boolean} options.enabled - Whether polling is active (default: true)
 */
const useHeartbeat = ( urlSlug, platform, {
  onMapStructureChange,
  onMapOccupancyChange,
  onResidenceVersionChange,
  onCommunityConfigChange,
  enabled = true,
  mode,
} = {} ) => {
  const mapStructureVersionRef = useRef( null );
  const mapOccupancyVersionRef = useRef( null );
  const residenceVersionRef = useRef( null );
  const communityConfigVersionRef = useRef( null );
  const intervalRef = useRef( null );
  const isMountedRef = useRef( true );

  const checkHeartbeat = useCallback( async () => {
    if ( !urlSlug || !isMountedRef.current ) {
      return;
    }

    try {
      const queryParams = mode ? { mode } : {};
      const url = buildYourTourApiUrl(
        `/your_tour_api/heartbeat/${urlSlug}`,
        queryParams,
        platform,
      );
      const data = await fetchYourTourApi( url, platform );

      if ( !data || !isMountedRef.current ) {
        return;
      }

      // On first response, store the baseline versions without triggering callbacks.
      if ( mapStructureVersionRef.current === null ) {
        mapStructureVersionRef.current = data.mapStructure;
        mapOccupancyVersionRef.current = data.mapOccupancy;
        residenceVersionRef.current = data.residence;
        communityConfigVersionRef.current = data.communityConfig;
        return;
      }

      // Check if map structure version changed (rare - SVG, levels, etc.).
      if ( data.mapStructure !== mapStructureVersionRef.current ) {
        mapStructureVersionRef.current = data.mapStructure;
        if ( onMapStructureChange ) {
          onMapStructureChange();
        }
      }

      // Check if map occupancy version changed (frequent - status changes).
      if ( data.mapOccupancy !== mapOccupancyVersionRef.current ) {
        mapOccupancyVersionRef.current = data.mapOccupancy;
        if ( onMapOccupancyChange ) {
          onMapOccupancyChange();
        }
      }

      // Check if residence version changed (status or residents).
      if ( data.residence !== residenceVersionRef.current ) {
        residenceVersionRef.current = data.residence;
        if ( onResidenceVersionChange ) {
          onResidenceVersionChange();
        }
      }

      // Check if community config version changed (community node updates).
      if ( data.communityConfig !== communityConfigVersionRef.current ) {
        communityConfigVersionRef.current = data.communityConfig;
        if ( onCommunityConfigChange ) {
          onCommunityConfigChange();
        }
      }
    } catch ( error ) {
      // Silently ignore heartbeat errors — this is a best-effort mechanism.
      // The existing version-check on page load still works as fallback.
    }
  }, [
    urlSlug,
    platform,
    mode,
    onMapStructureChange,
    onMapOccupancyChange,
    onResidenceVersionChange,
    onCommunityConfigChange,
  ] );

  useEffect( () => {
    if ( !urlSlug || !enabled ) {
      return undefined;
    }

    isMountedRef.current = true;

    const startPolling = () => {
      // Clear any existing interval before starting a new one.
      if ( intervalRef.current ) {
        clearInterval( intervalRef.current );
      }
      intervalRef.current = setInterval( checkHeartbeat, HEARTBEAT_INTERVAL_MS );
    };

    const stopPolling = () => {
      if ( intervalRef.current ) {
        clearInterval( intervalRef.current );
        intervalRef.current = null;
      }
    };

    const handleVisibilityChange = () => {
      if ( document.visibilityState === 'visible' ) {
        // Tab became visible — check immediately, then resume polling.
        checkHeartbeat();
        startPolling();
      } else {
        // Tab became hidden — stop polling to save resources.
        stopPolling();
      }
    };

    // Start polling and listen for visibility changes.
    checkHeartbeat();
    startPolling();
    document.addEventListener( 'visibilitychange', handleVisibilityChange );

    return () => {
      isMountedRef.current = false;
      stopPolling();
      document.removeEventListener( 'visibilitychange', handleVisibilityChange );
    };
  }, [ urlSlug, enabled, checkHeartbeat ] );
};

export default useHeartbeat;
